/**
 * The ledger on screen: the signed-in person's own on Personal, or the team's
 * when a team is picked, which everyone in it shares. Kept out of the board
 * store because none of it is board state: the board's change log never
 * carries it (server/finance.ts).
 *
 * Holds one calendar year at a time. A personal ledger is a few hundred lines
 * a year, so a year is one cheap request and every total is worked out here
 * from what is held, by the same `summarise()` the tests pin down.
 */
import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { api } from "../lib/api.js";
import { compareEntries, currencyDigits, nextDue, summarise, todayIso } from "@shared/finance.js";
import type { FinanceSettings, LedgerEntry, LedgerInput, Recurring, RecurringInput, ServerEvent } from "@shared/types.js";

interface FinancePage {
  year: number;
  entries: LedgerEntry[];
  recurring: Recurring[];
  settings: FinanceSettings;
  years: number[];
  categories: string[];
}

export const useFinance = defineStore("finance", () => {
  /** Whose ledger is held: a team's id, or null for the reader's own. */
  const teamId = ref<string | null>(null);
  const year = ref(new Date().getFullYear());
  const entries = reactive(new Map<string, LedgerEntry>());
  /** Every regular payment, whatever year is on screen: they are few, and none belongs to a year. */
  const recurring = reactive(new Map<string, Recurring>());
  const settings = ref<FinanceSettings>({ currency: null });
  /** Years with anything in them, from the server; the current one is always offered. */
  const years = ref<number[]>([]);
  const usedCategories = ref<string[]>([]);
  const loaded = ref(false);

  const sorted = computed(() => [...entries.values()].sort(compareEntries));
  const summary = computed(() => summarise(entries.values()));
  /** Running ones first, soonest due first; ended ones after, newest first. */
  const regulars = computed(() =>
    [...recurring.values()]
      .map((r) => ({ ...r, next: nextDue(r) }))
      .sort((a, b) =>
        a.next && b.next ? a.next.localeCompare(b.next) : a.next ? -1 : b.next ? 1 : b.start.localeCompare(a.start),
      ),
  );
  const digits = computed(() => (settings.value.currency ? currencyDigits(settings.value.currency) : 2));

  const yearOptions = computed(() => {
    const set = new Set([...years.value, new Date().getFullYear(), year.value]);
    return [...set].sort((a, b) => b - a);
  });

  let loading: Promise<void> | null = null;

  /** The query naming the ledger held, for every request: `?team=…&` or `?`. */
  const book = (): string => (teamId.value ? `?team=${encodeURIComponent(teamId.value)}&` : "?");

  /** Fetch a year, replacing what is held. */
  async function load(which = year.value): Promise<void> {
    const team = teamId.value;
    const run = (async () => {
      // `today` is ours, not the server's, so a payment due today posts on today.
      const page = await api.get<FinancePage>(`/api/finance${book()}year=${which}&today=${todayIso()}`);
      // A slower request for a year, or a ledger, the reader has since left must not land.
      if (which !== year.value || team !== teamId.value) return;
      entries.clear();
      for (const e of page.entries) entries.set(e.id, e);
      recurring.clear();
      for (const r of page.recurring) recurring.set(r.id, r);
      settings.value = page.settings;
      years.value = page.years;
      usedCategories.value = page.categories;
      loaded.value = true;
    })();
    loading = run;
    try {
      await run;
    } finally {
      if (loading === run) loading = null;
    }
  }

  /** Put a team's ledger — or, with null, the reader's own — on screen. */
  async function open(team: string | null): Promise<void> {
    if (team !== teamId.value) {
      reset();
      teamId.value = team;
    }
    await load();
  }

  async function showYear(next: number): Promise<void> {
    year.value = next;
    await load(next);
  }

  /** Keep a line only if it belongs to the year on screen. */
  function hold(entry: LedgerEntry): void {
    if (entry.date.startsWith(`${year.value}-`)) entries.set(entry.id, entry);
    else entries.delete(entry.id);
    if (entry.category && !usedCategories.value.includes(entry.category)) {
      usedCategories.value = [...usedCategories.value, entry.category];
    }
    const y = Number(entry.date.slice(0, 4));
    if (!years.value.includes(y)) years.value = [...years.value, y].sort((a, b) => b - a);
  }

  async function add(input: LedgerInput): Promise<LedgerEntry> {
    const { entry } = await api.post<{ entry: LedgerEntry }>(`/api/finance/entries${book()}`, input);
    hold(entry);
    return entry;
  }

  async function patch(id: string, input: Partial<LedgerInput>): Promise<LedgerEntry> {
    const { entry } = await api.patch<{ entry: LedgerEntry }>(`/api/finance/entries/${id}${book()}`, input);
    // The echo comes over the socket too, and on a team's ledger so may a
    // teammate's newer change, or their delete, before this reply does.
    const held = entries.get(id);
    if (held && held.updatedAt <= entry.updatedAt) hold(entry);
    return entry;
  }

  async function remove(id: string): Promise<void> {
    await api.del(`/api/finance/entries/${id}${book()}`);
    entries.delete(id);
  }

  async function addRecurring(input: RecurringInput): Promise<Recurring> {
    const res = await api.post<{ recurring: Recurring }>(`/api/finance/recurring${book()}today=${todayIso()}`, input);
    took(res.recurring);
    // The lines it posted arrive over the socket (`ledgerUpsert`), with the series
    // as posted (`recurringUpsert`); a socket that was down resyncs as it comes back.
    return res.recurring;
  }

  async function patchRecurring(id: string, input: Partial<RecurringInput>): Promise<Recurring> {
    const res = await api.patch<{ recurring: Recurring }>(`/api/finance/recurring/${id}${book()}today=${todayIso()}`, input);
    took(res.recurring);
    return res.recurring;
  }

  /** A write's reply: the series as it was BEFORE what was due was posted, so a
   *  later `postedThrough` the socket already brought is kept (it only moves on). */
  function took(r: Recurring): void {
    const held = recurring.get(r.id)?.postedThrough ?? null;
    recurring.set(r.id, held !== null && (r.postedThrough === null || held > r.postedThrough) ? { ...r, postedThrough: held } : r);
  }

  async function removeRecurring(id: string): Promise<void> {
    await api.del(`/api/finance/recurring/${id}${book()}`);
    forget(id);
  }

  /** A series is gone; the lines it posted stay, no longer pointing at it. */
  function forget(id: string): void {
    recurring.delete(id);
    for (const e of entries.values()) if (e.recurringId === id) entries.set(e.id, { ...e, recurringId: null });
  }

  async function setCurrency(currency: string): Promise<void> {
    const res = await api.put<{ settings: FinanceSettings }>(`/api/finance/settings${book()}`, { currency });
    settings.value = res.settings;
  }

  /** The owner's other tabs and devices — or the team's — told over the socket. */
  function apply(event: ServerEvent): void {
    if (!loaded.value || !("teamId" in event) || event.teamId !== teamId.value) return;
    if (event.t === "ledgerUpsert") hold(event.entry);
    else if (event.t === "ledgerDelete") entries.delete(event.id);
    else if (event.t === "recurringUpsert") recurring.set(event.recurring.id, event.recurring);
    else if (event.t === "recurringDelete") forget(event.id);
    else if (event.t === "financeSettings") settings.value = event.settings;
  }

  /**
   * After the socket comes back. Ledger events are never in the replayed change
   * log, so whatever changed while this tab was offline is only found by asking.
   */
  function resync(): void {
    if (loaded.value && !loading) void load().catch(() => undefined);
  }

  /** Also the CSV link, which is a plain download rather than an api call. */
  const exportUrl = computed(() => `/api/finance/export.csv${book()}year=${year.value}`);

  function reset(): void {
    teamId.value = null;
    entries.clear();
    recurring.clear();
    settings.value = { currency: null };
    years.value = [];
    usedCategories.value = [];
    loaded.value = false;
    year.value = new Date().getFullYear();
  }

  return {
    teamId, year, entries, recurring, settings, years, usedCategories, loaded,
    sorted, summary, regulars, digits, yearOptions, exportUrl,
    load, open, showYear, add, patch, remove, addRecurring, patchRecurring, removeRecurring, setCurrency, apply, resync, reset,
  };
});
